import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Book, Search, ChevronRight, Filter } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import AdBanner from '../components/ads/AdBanner';
import LoadingIndicator from '../components/common/LoadingIndicator';
import { fetchAllBareActs, searchBareActs, BareAct } from '../services/bareActService';

const BareActsPage: React.FC = () => {
  const [acts, setActs] = useState<BareAct[]>([]);
  const [filteredActs, setFilteredActs] = useState<BareAct[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const { t } = useLanguage();
  
  // Extract categories from acts
  const categories = useMemo(() => {
    const uniqueCategories = Array.from(new Set(acts.map(act => act.category)));
    return uniqueCategories;
  }, [acts]);

  useEffect(() => {
    document.title = 'Bare Acts | Legal Nest';
    
    const loadBareActs = async () => {
      try {
        setLoading(true);
        const data = await fetchAllBareActs();
        setActs(data);
        setFilteredActs(data);
      } catch (err) {
        console.error('Error fetching bare acts:', err);
        setError('Failed to load bare acts. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    loadBareActs();
  }, []);

  const handleSearch = useCallback(async (term: string, category: string) => {
    try {
      if (!term && category === 'all') {
        // Reset to all acts
        setFilteredActs(acts);
        return;
      }
      
      setSearchLoading(true);
      
      if (term) {
        // If there's a search term, use the search API
        const searchResults = await searchBareActs(term);
        
        // Further filter by category if needed
        if (category !== 'all') {
          setFilteredActs(searchResults.filter(act => act.category === category));
        } else {
          setFilteredActs(searchResults);
        }
      } else {
        // Just filter the already loaded acts by category
        if (category !== 'all') {
          setFilteredActs(acts.filter(act => act.category === category));
        } else {
          setFilteredActs(acts);
        }
      }
    } catch (err) {
      console.error('Error during search:', err);
      setError('Search failed. Please try again.');
    } finally {
      setSearchLoading(false);
    }
  }, [acts]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch(searchTerm, selectedCategory);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchTerm, selectedCategory, handleSearch]);

  // Skeleton loader
  const renderSkeletonCards = () => {
    return Array(6).fill(0).map((_, i) => (
      <div key={i} className="bg-white p-6 rounded-lg shadow-md animate-pulse">
        <div className="flex items-center mb-3">
          <div className="w-8 h-8 bg-neutral-200 rounded-full mr-2"></div>
          <div className="w-16 h-6 bg-neutral-200 rounded-full"></div>
        </div>
        <div className="h-6 bg-neutral-200 rounded mb-2 w-3/4"></div>
        <div className="h-4 bg-neutral-200 rounded mb-1 w-full"></div>
        <div className="h-4 bg-neutral-200 rounded mb-1 w-5/6"></div>
        <div className="h-4 bg-neutral-200 rounded mb-4 w-4/6"></div>
        <div className="flex justify-between items-center">
          <div className="h-4 bg-neutral-200 rounded w-1/4"></div>
          <div className="h-6 w-6 bg-neutral-200 rounded-full"></div>
        </div>
      </div>
    ));
  };

  return (
    <div className="container-custom py-8">
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-serif font-bold mb-4">
          {t('Bare Acts Library')}
        </h1>
        <p className="text-neutral-600">
          {t('Bare Acts Description')}
        </p>
      </div>
      
      <div className="flex flex-col md:flex-row gap-4 mb-8">
        {/* Search input */}
        <div className="relative w-full md:w-2/3">
          <input
            type="text"
            placeholder={t('search_bare_acts')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full p-3 pl-10 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            disabled={loading}
          />
          <Search className="absolute left-3 top-3 text-neutral-400" size={18} />
        </div>
        
        {/* Category filter */}
        <div className="relative w-full md:w-1/3">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full p-3 pl-10 border border-neutral-300 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-primary-500"
            disabled={loading}
          >
            <option value="all">{t('All Categories')}</option>
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
          <Filter className="absolute left-3 top-3 text-neutral-400" size={18} />
        </div>
      </div>
      
      {/* Search loading indicator */}
      {searchLoading && !loading && (
        <div className="flex items-center justify-center py-2 mb-4">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500 mr-2"></div>
          <span className="text-sm text-neutral-500">{t('updating_results')}</span>
        </div>
      )}
      
      {/* Main loading state */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {renderSkeletonCards()}
        </div>
      ) : error ? (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">
          {error}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredActs.map(act => (
              <Link 
                to={`/bare-acts/${act.slug}`}
                key={act.id}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mr-2">
                    <Book size={16} className="text-primary-600" />
                  </div>
                  <span className="text-xs font-medium px-2 py-1 bg-neutral-100 text-neutral-600 rounded-full">
                    {act.year}
                  </span>
                </div>
                <h3 className="font-serif font-bold mb-2 line-clamp-2">{act.title}</h3>
                <p className="text-neutral-600 text-sm mb-4 line-clamp-3">{act.description}</p>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-neutral-500">{act.category}</span>
                  <ChevronRight size={18} className="text-primary-500" />
                </div>
              </Link>
            ))}
          </div>
          
          {/* Empty state */}
          {filteredActs.length === 0 && (
            <div className="text-center py-12">
              <Book size={48} className="mx-auto text-neutral-300 mb-4" />
              <h3 className="text-xl font-medium mb-2">{t('no_bare_acts_found')}</h3>
              <p className="text-neutral-500">
                {t('try_adjusting_search')}
              </p>
            </div>
          )}
        </>
      )}
      
      {/* Ad banner */}
      <div className="mt-12">
        <AdBanner 
          id="bare-acts-bottom"
          slot="footer"
          size="leaderboard"
        />
      </div>
      
      {/* About section */}
      <div className="mt-12 p-6 bg-neutral-50 rounded-lg border border-neutral-200">
        <h2 className="text-xl font-serif font-bold mb-3">{t('About Bare Acts')}</h2>
        <p className="text-neutral-600">
          {t('Bare Acts Explanation')}
        </p>
      </div>
    </div>
  );
};

export default BareActsPage;